import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';
import pht from 'client-portal/utils/persistent-hash-table';

export default class LocaleManagerService extends Service {
  @service analytics;
  @service intl;
  @service currentPractice;
  @service fastboot;
  @service cookies;

  // Default locale, can be overridden by saved locale or browser locale
  @tracked currentLocale = 'en-us';

  availableLocales = [
    { code: 'en-us', name: 'English', flag: '🇺🇸' },
    { code: 'es-mx', name: 'Español', flag: '🇲🇽' },
  ];

  setLocale = (localeCode, method = 'user_selected') => {
    const previousLocale = this.currentLocale;

    if (this.availableLocales.find(locale => locale.code === localeCode)) {
      this.intl.setLocale([localeCode]);
      this.currentLocale = localeCode;

      this._trackLocaleSelected(localeCode, method);
      this._trackLocaleChanged(previousLocale, localeCode, method);
      this._persistLocale(localeCode);
    }
  };

  _trackLocaleSelected(localeCode, method) {
    // Track language usage and switching - only for Spanish locale events to avoid overwhelming tracking
    if (localeCode === 'es-mx') {
      this.analytics?.selectiveTrack('Locale Selected', {
        object: 'Locale',
        action: 'Selected',
        'locale_code': localeCode,
        method,
      });
    }
  }

  _trackLocaleChanged(previousLocale, localeCode, method) {
    // Only track locale changes involving Spanish (either from or to Spanish)
    if (
      previousLocale !== localeCode &&
      method === 'user_selected' &&
      (previousLocale === 'es-mx' || localeCode === 'es-mx')
    ) {
      this.analytics?.selectiveTrack('Locale Changed', {
        object: 'Locale',
        action: 'Changed',
        'from_locale': previousLocale,
        'to_locale': localeCode,
      });
    }
  }

  _persistLocale(localeCode) {
    // Persist locale preference in both cookie (for FastBoot) and localStorage (browser fallback)

    // Set cookie (available in both FastBoot and browser)
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30); // 30 days

    this.cookies.write('clientPortalLocale', localeCode, {
      expires: expirationDate,
      sameSite: 'lax',
    });

    // Also set localStorage for browser environments (backwards compatibility)
    if (!this.fastboot.isFastBoot) {
      pht.set('clientPortalLocale', localeCode);
    }
  }

  async initializeLocale() {
    // If internationalization is disabled, always use English
    if (!this.isEnabled) {
      this.intl.setLocale([this.currentLocale]);
      return;
    }

    const savedLocale = this._getSavedLocale();
    const browserLocale = this._getBrowserLocale();
    const defaultLocale = this._determineDefaultLocale(browserLocale);

    // Use saved locale if available, otherwise use browser/default
    const localeToUse = savedLocale || defaultLocale;
    const method = savedLocale ? 'saved_preference' : 'browser_default';

    this.setLocale(localeToUse, method);
  }

  _getSavedLocale() {
    // Try cookie first (works in both FastBoot and browser)
    const cookieValue = this.cookies.read('clientPortalLocale');
    if (cookieValue) {
      return cookieValue;
    }

    // Fallback to localStorage in browser environments only
    if (!this.fastboot.isFastBoot) {
      return pht.get('clientPortalLocale');
    }

    return null;
  }

  _getBrowserLocale() {
    // Get browser locale
    if (this.fastboot.isFastBoot) {
      // Server-side: parse Accept-Language header
      const acceptLanguage = this.fastboot.request.headers['accept-language'];
      if (acceptLanguage) {
        // Parse first language from Accept-Language header (e.g., "en-US,en;q=0.9,es;q=0.8")
        const primaryLanguage = acceptLanguage.split(',')[0].trim().toLowerCase();
        return primaryLanguage;
      }
      return null;
    } else {
      // Client-side: use navigator.language
      return navigator.language.toLowerCase();
    }
  }

  _determineDefaultLocale(browserLocale) {
    // Determine default locale based on browser language
    return (
      this.availableLocales.find(
        locale => browserLocale && browserLocale.startsWith(locale.code.split('-')[0])
      )?.code || this.currentLocale
    );
  }

  get currentLocaleName() {
    return (
      this.availableLocales.find(locale => locale.code === this.currentLocale)?.name || 'English'
    );
  }

  get currentLocaleFlag() {
    return this.availableLocales.find(locale => locale.code === this.currentLocale)?.flag || '🇺🇸';
  }

  get isEnabled() {
    // Check practice feature flag
    return this.currentPractice.get('featureClientPortalInternationalization') ?? false;
  }
}
